import {
  ActivityType,
  CallMode,
  CallOutcome,
  CallRecordingAction,
  DemoCallScenario,
  IAdhocActionResponse,
  IAvailablePhoneNumbersParams,
  IAvailablePhoneNumbersResponse,
  IBuyPhoneNumberRequest,
  ICallerIdsResponse,
  IOrganisationPhoneNumberItem,
  IPhoneNumberRegulationResponse,
  IWalletBalanceResponse,
  IPhoneNumbersResponse,
  IRegulatoryBundleItem,
  IUpdatePhoneNumberRequest,
  PhoneNumberCountry,
  RegulatoryEndUserType,
  ContactPersonSourceType,
  IContactActivitiesResponse,
  IContactAISummaryResponse,
  IContactApplicablePoliciesResponse,
  IContactPersonTagsResponse,
  IContactStatementResponse,
  ICallListParams,
  ICallListResponse,
  ICallFollowUpEmailDraft,
  ICallFollowUpEmailSendRequest,
  ICallFollowUpInvoice,
  ICallRecordedFollowUp,
  ICallStatusResponse,
  IPlaceCallResponse,
  ISingleContactResponse,
  StatementRelativePeriods,
  StatementType,
} from 'shared';

import { orgHeader } from '../../../../common/lib/axiosBaseQuery';
import { emptySplitApi } from '../baseApi';
import { buildNoteRequestBody } from '../noteFormData';
import { CacheTags } from '../../types/cacheTags';

export interface ContactPersonInput {
  id?: string;
  isPrimary: boolean;
  firstName?: string;
  lastName?: string;
  email?: string;
  mobileNumber?: string;
  mobileCountryCode?: string;
  defaultNumber?: string;
  defaultCountryCode?: string;
  includeInNotifications?: boolean;
  sourceType?: ContactPersonSourceType;
}

export const singleContactApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    fetchContact: builder.query<ISingleContactResponse, { organisationId: string; contactId: number }>({
      query: ({ organisationId, contactId }) => ({
        url: `contact/${contactId}`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      providesTags: result => {
        return result ?
          [
            { type: CacheTags.SingleContact as const, id: result.id },
          ]
          : [];
      },
    }),
    fetchContactStatement: builder.query<IContactStatementResponse, {
      organisationId: string;
      contactId: number;
      statementPeriod: StatementRelativePeriods;
      statementType?: StatementType;
      activityPeriodStart?: string;
      activityPeriodEnd?: string;
    }>({
      query: ({ organisationId, contactId, statementPeriod, statementType, activityPeriodStart, activityPeriodEnd }) => ({
        url: `contact/${contactId}/ui/statement`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
        params: {
          statementPeriod,
          type: statementType,
          start: statementType === StatementType.ACTIVITY ? activityPeriodStart : undefined,
          end: statementType === StatementType.ACTIVITY ? activityPeriodEnd : undefined,
        },
      }),
      providesTags: result => {
        return result ?
          [
            { type: CacheTags.ContactStatement as const, id: result?.statement?.contact?.id },
          ]
          : [];
      },
    }),
    fetchContactApplicablePolicies: builder.query<IContactApplicablePoliciesResponse, { organisationId: string; contactId: number }>({
      query: ({ organisationId, contactId }) => ({
        url: `contact/${contactId}/ui/applicable-policies`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      providesTags: result => {
        return result ?
          [
            { type: CacheTags.ContactApplicablePolicies as const },
          ]
          : [];
      },
    }),
    adhocInterestCharge: builder.mutation<IAdhocActionResponse, { organisationId: string; contactId: number }>({
      query: ({ organisationId, contactId }) => ({
        url: `contact/${contactId}/adhoc/statement-interest-charge`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [
        { type: CacheTags.Organisations, id: `LIST` },
        { type: CacheTags.SingleContact as const },
        { type: CacheTags.Actions, id: `LIST` },
        { type: CacheTags.BadContacts, id: `LIST` },
      ],
    }),

    // REPLACES SEND STATEMENT NOW
    adhocStatement: builder.mutation<IAdhocActionResponse, { organisationId: string; contactId: number; contactPersonIds?: string[] }>({
      query: ({ organisationId, contactId, contactPersonIds }) => ({
        url: `contact/${contactId}/adhoc/statement`,
        method: `POST`,
        data: contactPersonIds ? { contactPersonIds } : undefined,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [
        { type: CacheTags.Organisations, id: `LIST` },
        { type: CacheTags.SingleContact as const },
        { type: CacheTags.Actions, id: `LIST` },
        { type: CacheTags.BadContacts, id: `LIST` },
      ],
    }),

    renableCalls: builder.mutation<{ id: number }, { organisationId: string; contactId: number }>({
      query: ({ organisationId, contactId }) => ({
        url: `contact/${contactId}/enable-calls`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: result => [{ type: CacheTags.Organisations, id: `LIST` }, { type: CacheTags.SingleContact as const, id: result.id }],
    }),

    createContactNote: builder.mutation<void, { organisationId: string; contactId: number; content: string; mentionedUsers: string[]; attachments?: File[]; syncAttachments?: boolean }>({
      query: ({ organisationId, contactId, content, mentionedUsers, attachments, syncAttachments }) => {
        const body = buildNoteRequestBody({ content, mentionedUsers }, attachments, syncAttachments);

        return {
          url: `contact/${contactId}/note`,
          method: `POST`,
          data: body.data,
          headers: {
            [orgHeader]: organisationId,
            ...body.headers,
          },
        };
      },
      invalidatesTags: [{ type: CacheTags.SingleContact as const }],
    }),

    fetchContactActivities: builder.query<IContactActivitiesResponse, { organisationId: string; contactId: number }>({
      query: ({ organisationId, contactId }) => ({
        url: `contact/${contactId}/activities`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      providesTags: [{ type: CacheTags.ContactActivities as const }],
    }),

    createContactActivity: builder.mutation<void, { organisationId: string; contactId: number; type: ActivityType; callOutcome?: CallOutcome; note?: string; escalationId?: number; invoiceId?: string; outcome?: string; activityTime?: string; contactCallId?: string }>({
      query: ({ organisationId, contactId, type, callOutcome, note, escalationId, invoiceId, outcome, activityTime, contactCallId }) => ({
        url: `contact/${contactId}/activity`,
        method: `POST`,
        data: {
          type,
          callOutcome,
          note,
          escalationId,
          invoiceId,
          outcome,
          activityTime,
          contactCallId,
        },
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: (result, error, { invoiceId }) => [
        { type: CacheTags.ContactActivities as const },
        { type: CacheTags.SingleContact as const },
        { type: CacheTags.Escalations as const, id: `LIST` },
        ...(invoiceId ? [{ type: CacheTags.Invoices as const, id: invoiceId }] : []),
      ],
    }),

    fetchCallerIds: builder.query<ICallerIdsResponse, { organisationId: string; contactId: number }>({
      query: ({ organisationId, contactId }) => ({
        url: `contact/${contactId}/call/caller-ids`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      // Buying or releasing a number changes the options
      providesTags: [CacheTags.PhoneNumbers],
    }),

    placeCall: builder.mutation<IPlaceCallResponse, { organisationId: string; contactId: number; personId: string; field: `mobile` | `default`; mode: CallMode; fromNumber?: string; demoScenario?: DemoCallScenario }>({
      query: ({ organisationId, contactId, personId, field, mode, fromNumber, demoScenario }) => ({
        url: `contact/${contactId}/call`,
        method: `POST`,
        data: { personId, field, mode, fromNumber, demoScenario },
        headers: {
          [orgHeader]: organisationId,
        },
      }),
    }),

    cancelCall: builder.mutation<void, { organisationId: string; contactId: number; callId: string }>({
      query: ({ organisationId, contactId, callId }) => ({
        url: `contact/${contactId}/call/${callId}/cancel`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
    }),

    controlCallRecording: builder.mutation<void, { organisationId: string; contactId: number; callId: string; action: CallRecordingAction }>({
      query: ({ organisationId, contactId, callId, action }) => ({
        url: `contact/${contactId}/call/${callId}/recording`,
        method: `POST`,
        data: { action },
        headers: {
          [orgHeader]: organisationId,
        },
      }),
    }),

    fetchCallStatus: builder.query<ICallStatusResponse, { organisationId: string; contactId: number; callId: string }>({
      query: ({ organisationId, contactId, callId }) => ({
        url: `contact/${contactId}/call/${callId}`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
    }),

    fetchCallTranscript: builder.query<Pick<ICallStatusResponse, `transcript`>, { organisationId: string; contactId: number; callId: string }>({
      query: ({ organisationId, contactId, callId }) => ({
        url: `contact/${contactId}/call/${callId}/transcript`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
    }),

    fetchFollowUpInvoices: builder.query<ICallFollowUpInvoice[], { organisationId: string; contactId: number }>({
      query: ({ organisationId, contactId }) => ({
        url: `contact/${contactId}/follow-up-invoices`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
    }),

    fetchCallFollowUps: builder.query<ICallRecordedFollowUp[], { organisationId: string; contactId: number; callId: string }>({
      query: ({ organisationId, contactId, callId }) => ({
        url: `contact/${contactId}/call/${callId}/follow-ups`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      providesTags: (_result, _error, { callId }) => [{ type: CacheTags.CallFollowUps, id: callId }],
    }),

    draftCallFollowUpEmail: builder.mutation<ICallFollowUpEmailDraft, { organisationId: string; contactId: number; callId: string }>({
      query: ({ organisationId, contactId, callId }) => ({
        url: `contact/${contactId}/call/${callId}/follow-up-email/draft`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
    }),

    sendCallFollowUpEmail: builder.mutation<void, ICallFollowUpEmailSendRequest & { organisationId: string; contactId: number; callId: string }>({
      query: ({ organisationId, contactId, callId, ...data }) => ({
        url: `contact/${contactId}/call/${callId}/follow-up-email`,
        method: `POST`,
        data,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: (_result, _error, { callId }) => [{ type: CacheTags.CallFollowUps, id: callId }],
    }),

    fetchCalls: builder.query<ICallListResponse, ICallListParams & { organisationId: string }>({
      query: ({ organisationId, ...params }) => ({
        url: `calls`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
        params,
      }),
    }),

    fetchWalletBalance: builder.query<IWalletBalanceResponse, { organisationId: string }>({
      query: ({ organisationId }) => ({
        url: `calls/wallet`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      providesTags: [CacheTags.Wallet],
    }),

    fetchPhoneNumbers: builder.query<IPhoneNumbersResponse, { organisationId: string }>({
      query: ({ organisationId }) => ({
        url: `calls/numbers`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      providesTags: [CacheTags.PhoneNumbers],
    }),

    fetchPhoneNumberRegulation: builder.query<IPhoneNumberRegulationResponse, { organisationId: string; isoCountry: PhoneNumberCountry; endUserType?: RegulatoryEndUserType }>({
      query: ({ organisationId, isoCountry, endUserType }) => ({
        url: `calls/numbers/regulation`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
        params: { isoCountry, endUserType },
      }),
    }),

    fetchAvailablePhoneNumbers: builder.query<IAvailablePhoneNumbersResponse, IAvailablePhoneNumbersParams & { organisationId: string }>({
      query: ({ organisationId, ...params }) => ({
        url: `calls/numbers/available`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
        params,
      }),
    }),

    submitRegulatoryBundle: builder.mutation<IRegulatoryBundleItem, { organisationId: string; formData: FormData }>({
      query: ({ organisationId, formData }) => ({
        url: `calls/numbers/bundle`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
          'Content-Type': `multipart/form-data`,
        },
        data: formData,
      }),
      invalidatesTags: [CacheTags.PhoneNumbers],
    }),

    trackBuyPhoneNumberStarted: builder.mutation<void, { organisationId: string }>({
      query: ({ organisationId }) => ({
        url: `calls/numbers/buy-started`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
    }),

    buyPhoneNumber: builder.mutation<IOrganisationPhoneNumberItem, IBuyPhoneNumberRequest & { organisationId: string }>({
      query: ({ organisationId, ...data }) => ({
        url: `calls/numbers`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
        data,
      }),
      invalidatesTags: [CacheTags.PhoneNumbers],
    }),

    updatePhoneNumber: builder.mutation<IOrganisationPhoneNumberItem, IUpdatePhoneNumberRequest & { organisationId: string; id: string }>({
      query: ({ organisationId, id, ...data }) => ({
        url: `calls/numbers/${id}`,
        method: `PATCH`,
        headers: {
          [orgHeader]: organisationId,
        },
        data,
      }),
      invalidatesTags: [CacheTags.PhoneNumbers],
    }),

    releasePhoneNumber: builder.mutation<void, { organisationId: string; id: string }>({
      query: ({ organisationId, id }) => ({
        url: `calls/numbers/${id}`,
        method: `DELETE`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [CacheTags.PhoneNumbers],
    }),

    /** Org wide click to call settings. Read back through fetchSettings */
    updateCallSettings: builder.mutation<void, { organisationId: string; autoTranscribeCalls: boolean }>({
      query: ({ organisationId, ...data }) => ({
        url: `calls/settings`,
        method: `PUT`,
        headers: {
          [orgHeader]: organisationId,
        },
        data,
      }),
      invalidatesTags: [{ type: CacheTags.Settings, id: `LIST` }],
    }),

    toggleContactFreeze: builder.mutation<void, { organisationId: string; contactId: number }>({
      query: ({ organisationId, contactId }) => ({
        url: `contact/toggle-freeze`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
        data: {
          contactIds: [contactId],
        },
      }),
      invalidatesTags: [{ type: CacheTags.SingleContact as const }],
    }),

    assignContact: builder.mutation<void, { organisationId: string; contactId: number; userId: string }>({
      query: ({ organisationId, contactId, userId }) => ({
        url: `contact/${contactId}/assign/${userId}`,
        method: `PATCH`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [
        { type: CacheTags.SingleContact as const },
        { type: CacheTags.Contacts, id: `LIST` },
      ],
    }),

    updateContactPersons: builder.mutation<ISingleContactResponse, { organisationId: string; contactId: number; persons: ContactPersonInput[] }>({
      query: ({ organisationId, contactId, persons }) => ({
        url: `contact/${contactId}/persons`,
        method: `PUT`,
        data: {
          persons,
        },
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: result => [
        { type: CacheTags.SingleContact as const, id: result?.id },
        { type: CacheTags.Contacts, id: `LIST` },
      ],
    }),
    updateContactPersonTags: builder.mutation<IContactPersonTagsResponse, {
      organisationId: string;
      contactId: number;
      personId: string;
      tags: string[];
    }>({
      query: ({ organisationId, contactId, personId, tags }) => ({
        url: `contact/${contactId}/persons/${personId}/tags`,
        method: `PUT`,
        data: {
          tags,
        },
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: (result, error, { contactId }) => [
        { type: CacheTags.SingleContact as const, id: contactId },
        { type: CacheTags.Contacts, id: `LIST` },
      ],
    }),

    clearAssignedContact: builder.mutation<void, { organisationId: string; contactId: number }>({
      query: ({ organisationId, contactId }) => ({
        url: `contact/${contactId}/assign`,
        method: `DELETE`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [
        { type: CacheTags.SingleContact as const },
        { type: CacheTags.Contacts, id: `LIST` },
      ],
    }),

    bulkAssignContacts: builder.mutation<void, { organisationId: string; contactIds: number[]; userId: string | null }>({
      query: ({ organisationId, contactIds, userId }) => ({
        url: `contact/bulk-assign`,
        method: `POST`,
        data: { contactIds, userId },
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [
        { type: CacheTags.Contacts, id: `LIST` },
      ],
    }),

    fetchContactAISummary: builder.query<IContactAISummaryResponse, { organisationId: string; contactId: number }>({
      query: ({ organisationId, contactId }) => ({
        url: `contact/${contactId}/ai/summary`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      providesTags: [{ type: CacheTags.ContactAISummary as const }],
    }),
  }),
});

export const {
  useFetchContactQuery,
  useFetchContactStatementQuery,
  useLazyFetchContactQuery,
  useRenableCallsMutation,
  useCreateContactNoteMutation,
  useFetchContactActivitiesQuery,
  useCreateContactActivityMutation,
  usePlaceCallMutation,
  useCancelCallMutation,
  useControlCallRecordingMutation,
  useFetchCallStatusQuery,
  useLazyFetchCallStatusQuery,
  useFetchCallTranscriptQuery,
  useFetchCallFollowUpsQuery,
  useDraftCallFollowUpEmailMutation,
  useSendCallFollowUpEmailMutation,
  useFetchFollowUpInvoicesQuery,
  useFetchCallsQuery,
  useFetchCallerIdsQuery,
  useFetchWalletBalanceQuery,
  useFetchPhoneNumbersQuery,
  useLazyFetchPhoneNumberRegulationQuery,
  useLazyFetchAvailablePhoneNumbersQuery,
  useSubmitRegulatoryBundleMutation,
  useBuyPhoneNumberMutation,
  useTrackBuyPhoneNumberStartedMutation,
  useUpdatePhoneNumberMutation,
  useUpdateCallSettingsMutation,
  useReleasePhoneNumberMutation,
  useAdhocInterestChargeMutation,
  useAdhocStatementMutation,
  useToggleContactFreezeMutation,
  useFetchContactApplicablePoliciesQuery,
  useFetchContactAISummaryQuery,
  useAssignContactMutation,
  useUpdateContactPersonsMutation,
  useUpdateContactPersonTagsMutation,
  useClearAssignedContactMutation,
  useBulkAssignContactsMutation,
} = singleContactApi;
